import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Button from '@lib/components/Button/Button';
import { PATH_LATEST_VERSION } from '@lib/enums/urls';
import useIsOnline from '@lib/hooks/useIsOnline';
import { logger } from '@lib/services/logger';
import {
  onVersionMismatch,
  offVersionMismatch,
} from '@lib/utils/versionMismatchEvent';
import styles from './ReleaseTracker.module.scss';

const VERSION_MISMATCH = 'mismatch';

type Props = {
  captureException: (error: Error) => void;
  domain: string;
  releaseVersion: string;
  releaseTrackerSeconds: string | number;
};

export default function ReleaseTracker(props: Props) {
  const { captureException, releaseTrackerSeconds, releaseVersion, domain } =
    props;
  const { t } = useTranslation();
  const [lastReleaseVersion, setLastReleaseVersion] = useState<string | null>(
    null,
  );
  const { pathname, search } = useLocation();
  const lastFetchTimeRef = useRef<Date | null>(null);
  const isOnline = useIsOnline();

  const isMismatch = lastReleaseVersion === VERSION_MISMATCH;
  const isMismatchRef = useRef(isMismatch);
  isMismatchRef.current = isMismatch;

  const isModalAvailable =
    isMismatch || (!!releaseVersion && !!lastReleaseVersion);
  const isModalOpen = isMismatch || releaseVersion !== lastReleaseVersion;

  const intervalMs =
    typeof releaseTrackerSeconds === 'string'
      ? (parseInt(releaseTrackerSeconds, 10) || 0) * 1000
      : Number(releaseTrackerSeconds || 0) * 1000;

  const isEnabled = !!domain && !!releaseVersion && intervalMs > 0;

  const fetchVersionCheck = useCallback(async () => {
    if (!isEnabled || isMismatchRef.current) return;
    try {
      lastFetchTimeRef.current = new Date();
      const response = await fetch(`${domain}/${PATH_LATEST_VERSION}.txt`, {
        cache: 'reload',
      });
      if (!response.ok) return;
      const content = await response.text();
      const contentValue = content.trim();
      logger.debug('ReleaseTracker: version check', {
        lastVersion: contentValue,
        releaseVersion,
      });
      if (contentValue) setLastReleaseVersion(contentValue);
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }, [captureException, domain, isEnabled, releaseVersion]);

  // Check on route changes (with throttle to avoid double-fetch with interval)
  useEffect(() => {
    const shouldFetch = ((): boolean => {
      if (!isEnabled) return false;
      if (!lastFetchTimeRef.current) return true;
      const diff = Date.now() - lastFetchTimeRef.current.getTime();
      return diff >= intervalMs;
    })();

    if (shouldFetch && isOnline) {
      fetchVersionCheck();
    }
  }, [fetchVersionCheck, intervalMs, isEnabled, isOnline, pathname, search]);

  // Periodic polling via setInterval
  useEffect(() => {
    if (!isEnabled || !isOnline || !intervalMs) return undefined;

    const id = setInterval(() => {
      fetchVersionCheck();
    }, intervalMs);

    return () => clearInterval(id);
  }, [fetchVersionCheck, intervalMs, isEnabled, isOnline]);

  // Immediate check when a new service worker activates
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !isEnabled) return undefined;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'SERVICE_WORKER_READY') {
        fetchVersionCheck();
      }
    };

    const sw = (navigator as unknown as Navigator).serviceWorker;
    sw.addEventListener('message', handler);
    return () => sw.removeEventListener('message', handler);
  }, [fetchVersionCheck, isEnabled]);

  // Show modal immediately when a GraphQL schema mismatch is detected.
  // The error itself proves the cached frontend is outdated — no need to
  // fetch latest-version.txt (it may not even exist in dev).
  useEffect(() => {
    const handler = () => {
      setLastReleaseVersion(VERSION_MISMATCH);
    };

    onVersionMismatch(handler);
    return () => offVersionMismatch(handler);
  }, []);

  if (!isModalAvailable) return null;

  const reload = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('ts', String(Date.now()));

    // Force fetch fresh index.html to update browser HTTP cache
    try {
      await fetch(url.origin + url.pathname, { cache: 'reload' });
    } catch (error) {
      logger.warn('ReleaseTracker: failed to prefetch index.html', {
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Clear navigation cache so SW fetches fresh index.html on reload.
    try {
      await caches.delete('navigations');
    } catch {
      // ignore
    }

    window.location.replace(url.toString());
  };

  const modalActions = (
    <div className={styles.actions}>
      <Button
        key="refresh"
        leftIcon="refresh"
        buttonText={t('refresh-page')}
        onClick={reload}
      />
    </div>
  );

  return (
    <Modal
      isOpen={isModalAvailable && isModalOpen}
      title={t('release-tracker-title')}
      description={t('release-tracker-desc')}
      actions={modalActions}
    />
  );
}
